import React from 'react';
import PropTypes from 'prop-types';
import Layout from '../../components/Layout';
import NetSales from '../../components/NetSales';
import TopGrossingServices from '../../components/TopGrossingServices';
import SoldTopList from '../../components/SoldTopList';
import TotalCollected from '../../components/TotalCollected';
import TotalSalesBreakdown from '../../breakdown/TotalSalesBreakdown';
import SalesByServiceFilers from '../containers/SalesByServiceFilters';
import BarberFilter from '../../metrics/barberFilterContainer';
import ServiceCategoryFilter from '../../filters/ServiceCategoryFilter';
import DirtyFilterOverlay from '../../components/DirtyFilterOverlay';
import CollectedByPaymentList from '../../components/CollectedByPaymentList';
import {
  RoleAllowBrand,
  RoleAwareShop,
  RoleDenyBrand,
} from '../../../../auth/roleAware';
import CountAndAmountBlock from '../../components/CountAndAmountBlock';
import ShopFilter from '../../filters/ShopFilter';
import { taxBreakdownArray } from '../../shapes';

const SalesByService = ({
  canceledAppointmentsCount,
  canceledAppointmentsAmount,
  noShowAppointmentsCount,
  noShowAppointmentsAmount,
  serviceSecuredDepositsCount,
  serviceSecuredDepositsAmount,
  discounts,
  fees,
  grossSales,
  netSales,
  refunds,
  mostBookedServices,
  servicesChartData,
  taxes,
  tips,
  totalCollected,
  loanRepayment,
  serviceSales,
  unchargedAppointmentsCount,
  unchargedAppointmentsAmount,
  taxBreakdown,
  paymentDepositsEnabled,
}) => {
  const className = paymentDepositsEnabled ? "sales-by-service-deposits-grid" : "sales-by-service-grid";

  return (
    <Layout title="Sales by Service">
      <div className="c-sales-by-service-wrapper">
        <div className="c-report-filters">
          <div className="u-mr0 large-and-up-mr- u-mb">
            <SalesByServiceFilers
              showCsvExport
              csvJobName="service-report"
              csvModalTitle="Sales by Service"
              disableBarberFilter
            >
              <RoleAllowBrand>
                <ShopFilter />
              </RoleAllowBrand>
              <RoleDenyBrand>
                <RoleAwareShop>
                  <div className="c-report-filters__item">
                    <BarberFilter />
                  </div>
                </RoleAwareShop>
              </RoleDenyBrand>
              <RoleDenyBrand>
                <div className="c-report-filters__item">
                  <ServiceCategoryFilter removeFilters={[]} />
                </div>
              </RoleDenyBrand>
            </SalesByServiceFilers>
          </div>
        </div>
        <div className="u-position-relative">
          <DirtyFilterOverlay style={{ left: "-20px" }} />
          <div className="layout u-pt+">
            <div className="layout__item u-1/2-extralarge-and-up">
              <TopGrossingServices data={servicesChartData} />
            </div>
            <div className="layout__item u-1/4-extralarge-and-up small-mt0 extralarge-and-down-mt+">
              <SoldTopList items={mostBookedServices} />
            </div>
            <div className="layout__item u-1/4-extralarge-and-up small-mt0 extralarge-and-down-mt+">
              <CollectedByPaymentList items={serviceSales} />
            </div>
          </div>
          <div className={`c-${className} u-mt extralarge-and-up-mt+`}>
            <div className={`c-${className}__net-sales`}>
              <NetSales
                kind="service"
                totalSales={grossSales}
                refunds={refunds}
                discounts={discounts}
                netSales={netSales}
              />
            </div>
            <div className={`c-${className}__total-collected`}>
              <TotalCollected
                netSales={netSales}
                fees={fees}
                taxes={taxes}
                tips={tips}
                totalCollected={totalCollected}
                loanRepayment={loanRepayment}
                taxBreakdown={taxBreakdown}
              />
            </div>
            <div className={`c-${className}__canceled-appointments`}>
              <CountAndAmountBlock
                title="Canceled"
                count={canceledAppointmentsCount}
                amount={canceledAppointmentsAmount}
                automationIdPart="Cancelled"
              />
            </div>
            <div className={`c-${className}__no-shows-appointments`}>
              <CountAndAmountBlock
                title="No Shows"
                count={noShowAppointmentsCount}
                amount={noShowAppointmentsAmount}
                automationIdPart="noShows"
              />
            </div>
            {paymentDepositsEnabled && (
              <div className={`c-${className}__secured-deposits`}>
                <CountAndAmountBlock
                  title="Booking Deposits"
                  count={serviceSecuredDepositsCount}
                  amount={serviceSecuredDepositsAmount}
                  automationIdPart="securedDeposits"
                />
              </div>
            )}
            <div className={`c-${className}__uncharged-appointments`}>
              <CountAndAmountBlock
                title="Not Marked"
                count={unchargedAppointmentsCount}
                amount={unchargedAppointmentsAmount}
                automationIdPart="NotMarked"
              />
            </div>
          </div>
          <div className="u-mt+">
            <TotalSalesBreakdown showCountInTotal totalTitle={"Total Services"} serviceProductTitle={"Services"} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

SalesByService.propTypes = {
  canceledAppointmentsCount: PropTypes.number.isRequired,
  canceledAppointmentsAmount: PropTypes.number.isRequired,
  noShowAppointmentsCount: PropTypes.number.isRequired,
  noShowAppointmentsAmount: PropTypes.number.isRequired,
  serviceSecuredDepositsCount: PropTypes.number.isRequired,
  serviceSecuredDepositsAmount: PropTypes.number.isRequired,
  discounts: PropTypes.number.isRequired,
  fees: PropTypes.number.isRequired,
  grossSales: PropTypes.number.isRequired,
  netSales: PropTypes.number.isRequired,
  refunds: PropTypes.number.isRequired,
  mostBookedServices: PropTypes.arrayOf(PropTypes.object).isRequired,
  servicesChartData: PropTypes.arrayOf(PropTypes.object).isRequired,
  taxes: PropTypes.number.isRequired,
  tips: PropTypes.number.isRequired,
  totalCollected: PropTypes.number.isRequired,
  loanRepayment: PropTypes.number.isRequired,
  serviceSales: PropTypes.object.isRequired,
  unchargedAppointmentsCount: PropTypes.number.isRequired,
  unchargedAppointmentsAmount: PropTypes.number.isRequired,
  taxBreakdown: taxBreakdownArray.isRequired,
};

export default SalesByService;
